import * as THREE from "three";

// Chaingun ground impact — must be visible from ~200m altitude.
// Two layers: a white flash (instant, 8m) + dirt cloud (expands to 15m, 0.6s).

const POOL_SIZE = 24;
const LIFETIME   = 0.55;
const FLASH_DUR  = 0.12;

type ImpactSlot = {
  flash: THREE.Mesh;
  dust:  THREE.Mesh;
  ring:  THREE.Mesh;
  light: THREE.PointLight;
  life:  number;
  active: boolean;
};

const flashMat = new THREE.MeshBasicMaterial({ color: 0xffffaa, transparent: true, opacity: 0, depthWrite: false });
const dustMat  = new THREE.MeshBasicMaterial({ color: 0xb09060, transparent: true, opacity: 0, depthWrite: false });
const ringMat  = new THREE.MeshBasicMaterial({ color: 0xffd48d, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide });

export class ImpactManager {
  private readonly pool: ImpactSlot[] = [];

  constructor(private readonly scene: THREE.Scene) {
    const ballGeom = new THREE.SphereGeometry(1, 7, 7);
    ballGeom.scale(1, 0.6, 1);
    const ringGeom = new THREE.RingGeometry(0.84, 1, 16);

    for (let i = 0; i < POOL_SIZE; i++) {
      const flash = new THREE.Mesh(ballGeom, flashMat.clone());
      const dust  = new THREE.Mesh(ballGeom, dustMat.clone());
      const ring  = new THREE.Mesh(ringGeom, ringMat.clone());
      flash.userData["isVFX"] = true;
      dust.userData["isVFX"]  = true;
      ring.userData["isVFX"]  = true;
      flash.visible = dust.visible = ring.visible = false;
      ring.rotation.x = -Math.PI / 2;

      const light = new THREE.PointLight(0xffee88, 0, 60);
      flash.add(light);

      this.scene.add(flash, dust, ring);
      this.pool.push({ flash, dust, ring, light, life: 0, active: false });
    }
  }

  spawn(point: THREE.Vector3): void {
    const slot = this.pool.find((s) => !s.active);
    if (!slot) return;

    const p = point.clone();
    p.y = Math.max(p.y, 0.1);

    slot.flash.position.copy(p);
    slot.dust.position.copy(p);
    slot.ring.position.copy(p);
    slot.ring.position.y += 0.03;
    slot.life   = LIFETIME;
    slot.active = true;

    // Initial scales
    slot.flash.scale.setScalar(3);
    slot.dust.scale.setScalar(1);
    slot.ring.scale.setScalar(1.4);
    slot.flash.visible = slot.dust.visible = slot.ring.visible = true;
    slot.light.intensity = 8;
  }

  update(dt: number): void {
    for (const s of this.pool) {
      if (!s.active) continue;

      s.life -= dt;
      if (s.life <= 0) {
        s.active = false;
        s.flash.visible = s.dust.visible = s.ring.visible = false;
        s.light.intensity = 0;
        continue;
      }

      const age     = LIFETIME - s.life;
      const tTotal  = s.life / LIFETIME;        // 1→0

      // Flash — bright pop for first FLASH_DUR seconds
      const flashT = Math.max(0, 1 - age / FLASH_DUR);
      s.flash.visible = flashT > 0.01;
      (s.flash.material as THREE.MeshBasicMaterial).opacity = flashT * 0.9;
      s.flash.scale.set(3 + (1 - flashT) * 5, 1.2 + (1 - flashT) * 1.8, 3 + (1 - flashT) * 5);
      s.light.intensity = flashT * 10;
      s.ring.visible = flashT > 0.01;
      (s.ring.material as THREE.MeshBasicMaterial).opacity = flashT * 0.55;
      s.ring.scale.setScalar(1.4 + (1 - flashT) * 5.4);

      // Dust cloud — expands to 15m, rises, fades
      s.dust.visible = true;
      const expand = Math.min(age / LIFETIME, 1);
      s.dust.scale.set(1 + expand * 7.5, 0.8 + expand * 1.8, 1 + expand * 7.5);
      s.dust.position.y += 3.1 * dt;
      (s.dust.material as THREE.MeshBasicMaterial).opacity = tTotal * 0.72;
    }
  }

  dispose(): void {
    for (const s of this.pool) this.scene.remove(s.flash, s.dust, s.ring);
  }
}
